import * as React from 'react';
import { withApollo } from 'react-apollo';

import type { ApolloClient, ApolloQueryResult } from '@apollo/client';
import $ from 'jquery';
import { compose, setDisplayName, withProps } from 'recompose';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import type { InjectedRouter } from 'js/lib/connectToRouter';
import connectToRouter from 'js/lib/connectToRouter';
import Retracked from 'js/lib/retracked';
import type { UserData } from 'js/lib/user';
import user from 'js/lib/user';
import waitForGraphQL from 'js/lib/waitForGraphQL';

import { Grid, MenuList, PageNotification } from '@coursera/cds-core';
import { BriefcaseIcon, CalendarIcon, CurriculumIcon, EmailIcon, UserIcon } from '@coursera/cds-icons';
import { track } from '@coursera/event-pulse/sdk';
import type { UserEmail } from '@coursera/grpc-types-useremails/coursera/proto/useremails/v1/email_address';

import SettingsActionCreators from 'bundles/account-settings/actions/SettingsActionCreators';
import SSOAccountLoginInformation from 'bundles/account-settings/components/SSOAccountLoginInformation';
import AccountLinkingPreferences from 'bundles/account-settings/components/sections/account-linking/AccountLinkingPreferences';
import BasicInformation from 'bundles/account-settings/components/sections/basic-information/BasicInformation';
import CalendarSettings from 'bundles/account-settings/components/sections/calendar/CalendarSettings';
import DataReport from 'bundles/account-settings/components/sections/data-report/DataReport';
import AccountDeletion from 'bundles/account-settings/components/sections/delete-account/AccountDeletion';
import DeletionConfirmation from 'bundles/account-settings/components/sections/delete-account/DeletionConfirmation';
import EmailPreferences from 'bundles/account-settings/components/sections/email-preferences/EmailPreferences';
import NotesAndHighlightingSettings from 'bundles/account-settings/components/sections/highlights/NotesAndHighlightingSettings';
import NavItem from 'bundles/account-settings/components/sections/navigation/NavItem';
import CreditCardSettings from 'bundles/account-settings/components/sections/payment-method/CreditCardSettings';
import RecoveryEmail from 'bundles/account-settings/components/sections/recovery-email/RecoveryEmail';
import Password from 'bundles/account-settings/components/sections/reset-password/Password';
import PersonalAccount from 'bundles/account-settings/components/sections/reset-password/PersonalAccount';
import SessionInvalidation from 'bundles/account-settings/components/sections/session-invalidation';
import TwoFactorAuthentication from 'bundles/account-settings/components/sections/two-factor/Settings';
import SettingsConstants from 'bundles/account-settings/constants/SettingsConstants';
import type { UserForcedSSOContextType } from 'bundles/account-settings/contexts/UserForcedSSOContext';
import {
  UserForcedSSOContextConsumer,
  UserForcedSSOContextProvider,
} from 'bundles/account-settings/contexts/UserForcedSSOContext';
import type {
  FindUserEmailsByUserIdQueryResult,
  FindUserEmailsByUserIdQueryVariables,
} from 'bundles/account-settings/queries';
import { FindUserEmailsByUserIdQuery } from 'bundles/account-settings/queries';
import SettingsStore from 'bundles/account-settings/stores/SettingsStore';
import { getSecondaryEmail } from 'bundles/account-settings/utils/RecoveryEmailUtils';
import { SettingsQuery, SettingsQueryOptions } from 'bundles/account-settings/utils/SettingsGraphqlQueries';
import Naptime from 'bundles/naptimejs';
import GdprSettings from 'bundles/naptimejs/resources/gdprSettings.v1';
import ThirdPartyOrganizationsV1 from 'bundles/naptimejs/resources/thirdPartyOrganizations.v1';
import PageFooter from 'bundles/page-footer/components/PageFooter';
import PageHeader from 'bundles/page-header/components/PageHeader';
import { CourseraMetatags } from 'bundles/seo';
import JumioVerificationSettings from 'bundles/verification/components/JumioVerificationSettings';

import _t from 'i18n!nls/account-settings';

import 'css!./__styles__/SettingsApp';
import 'css!bundles/account-settings/styl/index';

const { Sections, UpdateStates } = SettingsConstants;

type TOP_SECTION_VALUE_TYPES =
  (typeof SettingsConstants)['TopSections'][keyof (typeof SettingsConstants)['TopSections']];

type Props = {
  userData: UserData;
  showPersonalAccountSection: boolean;
  showPasswordSection: boolean;
  showAccountDeletionSection: boolean;
  secondaryEmail: UserEmail | undefined;
  hasGeneratedPassword: boolean;
  userEmails: UserEmail[];
  router: InjectedRouter;
  client: ApolloClient<unknown>;
  ssoOrg: ThirdPartyOrganizationsV1 | undefined;
  refetchUserEmails: (() => Promise<ApolloQueryResult<FindUserEmailsByUserIdQueryResult>>) | undefined;
};

type SettingsAppProps = Props & UserForcedSSOContextType;

type State = {
  emailAddress?: string;
  fullName?: string;
  locale?: string;
  timezone?: string;
  userId?: number;
  isAccountDeleted?: boolean;
  hasPendingChanges: boolean;
  topSection?: string;
  notificationPreferences?: any;
  updateStatus?: any;
  invalidFields?: any;
  updatedEmailAddress?: any;
  creditCardPreferences?: any;
  wallets?: any;
  showDeleteCreditCardModal?: boolean;
  activeWalletId?: any;
  emailPreferences?: any;
  twoFactorAuthenticationStatus?: string;
  twoFactorAuthenticationQRCode?: any;
  twoFactorAuthenticationInvalidFields?: any[];
  twoFactorAuthenticationIsUpdating?: boolean;
};

export class SettingsApp extends React.Component<SettingsAppProps, State> {
  state: State = {
    hasPendingChanges: false,
  };

  settingsContentRef = React.createRef<HTMLDivElement>();

  constructor(props: SettingsAppProps) {
    super(props);

    const { userData } = this.props;

    this.state = {
      emailAddress: userData.email_address,
      fullName: userData.fullName,
      locale: userData.locale,
      timezone: userData.timezone,
      userId: userData.id,
      isAccountDeleted: false,
      hasPendingChanges: false,
    };
    this.focusSettingsContentRef = this.focusSettingsContentRef.bind(this);
  }

  focusSettingsContentRef() {
    this.settingsContentRef.current?.focus();
  }

  componentDidMount() {
    const { router, userData, client } = this.props;
    SettingsStore.addListener('change', this.onChange);
    SettingsActionCreators.setUserData(userData);
    SettingsActionCreators.getUserSettings(userData.id, client);
    if (router?.location?.hash) {
      // Subset of SettingsConstants
      const sectionName = router.location.hash.substr(1) as 'Payment' | 'Email' | 'Highlights' | 'Calendar';
      if (
        [
          SettingsConstants.TopSections.EMAIL,
          SettingsConstants.TopSections.HIGHLIGHTS,
          SettingsConstants.TopSections.CALENDAR,
        ].includes(sectionName)
      ) {
        SettingsActionCreators.setTopSection(sectionName);
      } else {
        SettingsActionCreators.setTopSection(SettingsConstants.TopSections.ACCOUNT);
      }
      router.replace('/account-settings');
    } else {
      SettingsActionCreators.setTopSection(SettingsConstants.TopSections.ACCOUNT);
    }
    this.onChange();
    this.hasCompletedLoading();
    this.updateWindowListener();
  }

  componentDidUpdate() {
    this.updateWindowListener();
  }

  componentWillUnmount() {
    SettingsStore.removeListener('change', this.onChange);
  }

  handleWindowClose = () => {
    return _t('You will lose your unsaved changes.');
  };

  onChange = () => {
    try {
      this.setState({
        topSection: SettingsStore.getTopSection(),
        notificationPreferences: SettingsStore.getNotificationPreferences(),
        userId: SettingsStore.getUserId(),
        fullName: SettingsStore.getFullName(),
        emailAddress: SettingsStore.getEmailAddress(),
        timezone: SettingsStore.getTimezone(),
        locale: SettingsStore.getLocale(),
        updateStatus: SettingsStore.getUpdateStatus(),
        invalidFields: SettingsStore.getInvalidFields(),
        updatedEmailAddress: SettingsStore.getUpdatedEmailAddress(),
        creditCardPreferences: SettingsStore.getCreditCardPreferences(),
        wallets: SettingsStore.getPaymentWallets(),
        showDeleteCreditCardModal: SettingsStore.getShowDeleteCreditCardModal(),
        activeWalletId: SettingsStore.getActiveWalletId(),
        emailPreferences: SettingsStore.getEmailPreferences(),
        hasPendingChanges: SettingsStore.hasPendingChanges(),
        twoFactorAuthenticationStatus: SettingsStore.getTwoFactorAuthenticationStatus(),
        twoFactorAuthenticationQRCode: SettingsStore.getTwoFactorAuthenticationQRCode(),
        twoFactorAuthenticationInvalidFields: SettingsStore.getTwoFactorAuthenticationInvalidFields(),
        twoFactorAuthenticationIsUpdating: SettingsStore.isTwoFactorAuthenticationUpdating(),
        isAccountDeleted: SettingsStore.isAccountDeleted(),
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      throw e;
    }
  };

  getUpdateStatus = (section: $TSFixMe) => {
    const { updateStatus } = this.state;

    return updateStatus && updateStatus.section && updateStatus.section === section
      ? updateStatus
      : { state: UpdateStates.IDLE };
  };

  updateWindowListener = () => {
    const { hasPendingChanges } = this.state;
    if (hasPendingChanges) {
      window.onbeforeunload = this.handleWindowClose;
    } else {
      window.onbeforeunload = null;
    }
  };

  /**
   * Remove the loading icon once the component
   * has been successfully loaded.
   */
  hasCompletedLoading = () => {
    $('[data-js=origami]').remove();
  };

  showAccountSection = () => {
    SettingsActionCreators.setTopSection(SettingsConstants.TopSections.ACCOUNT);
    this.focusSettingsContentRef();
  };

  showPaymentSection = () => {
    SettingsActionCreators.setTopSection(SettingsConstants.TopSections.PAYMENT);
    this.focusSettingsContentRef();
  };

  showEmailSection = () => {
    SettingsActionCreators.setTopSection(SettingsConstants.TopSections.EMAIL);
    this.focusSettingsContentRef();
    track('click_button', {
      button: { name: 'communication_preferences' },
    });
  };

  showHighlightsSection = () => {
    SettingsActionCreators.setTopSection(SettingsConstants.TopSections.HIGHLIGHTS);
    this.focusSettingsContentRef();
  };

  showCalendarSection = () => {
    SettingsActionCreators.setTopSection(SettingsConstants.TopSections.CALENDAR);
    this.focusSettingsContentRef();
  };

  renderNavigation = () => {
    const { topSection, wallets } = this.state;
    const { TopSections } = SettingsConstants;

    const items: {
      key: TOP_SECTION_VALUE_TYPES;
      label: string;
      icon: React.ReactNode;
      onClick: () => void;
    }[] = [
      { key: TopSections.ACCOUNT, label: _t('Account'), icon: <UserIcon />, onClick: this.showAccountSection },
      ...((wallets?.length || 0) > 0
        ? [
            {
              key: TopSections.PAYMENT,
              label: _t('Payment'),
              icon: <BriefcaseIcon />,
              onClick: this.showPaymentSection,
            },
          ]
        : []),
      {
        key: TopSections.EMAIL,
        label: _t('Communication Preferences'),
        icon: <EmailIcon />,
        onClick: this.showEmailSection,
      },
      {
        key: TopSections.HIGHLIGHTS,
        label: _t('Notes & Highlights'),
        icon: <CurriculumIcon />,
        onClick: this.showHighlightsSection,
      },
    ];

    items.push({
      key: TopSections.CALENDAR,
      label: _t('Calendar Sync'),
      icon: <CalendarIcon />,
      onClick: this.showCalendarSection,
    });

    return items.map(({ key, label, icon, onClick }) => (
      <NavItem key={key} isActive={key === topSection} label={label} icon={icon} onClick={onClick} />
    ));
  };

  renderAccountSection() {
    const {
      showPersonalAccountSection,
      showPasswordSection,
      showAccountDeletionSection,
      secondaryEmail,
      hasGeneratedPassword,
      isUserSsoLoginForced,
      ssoOrg,
      refetchUserEmails,
      userEmails,
    } = this.props;
    const {
      emailAddress,
      fullName,
      invalidFields,
      twoFactorAuthenticationInvalidFields,
      twoFactorAuthenticationIsUpdating,
      twoFactorAuthenticationQRCode,
      twoFactorAuthenticationStatus,
      updatedEmailAddress,
      userId,
    } = this.state;

    const ssoOrgName = ssoOrg?.name;

    return (
      <div>
        {isUserSsoLoginForced && !secondaryEmail && (
          <div className="section-wrapper">
            <PageNotification severity="warning" title={_t('Add a recovery email')}>
              {ssoOrgName
                ? _t(
                    'Add a personal recovery email to ensure access to your accomplishments in case you lose access to your #{ssoOrgName} account.',
                    { ssoOrgName }
                  )
                : _t(
                    'Add a personal recovery email to ensure access to your accomplishments in case you lose access to your organization account.'
                  )}
            </PageNotification>
          </div>
        )}

        <h1 className="display-2-text top-section-title" id="a11y-navigation-heading">
          {_t('Account')}
        </h1>

        {isUserSsoLoginForced && (
          <div className="section-wrapper">
            <SSOAccountLoginInformation ssoOrgName={ssoOrgName} />
          </div>
        )}

        <div className="section-wrapper">
          <BasicInformation
            fullName={fullName}
            emailAddress={emailAddress}
            updateStatus={this.getUpdateStatus(Sections.BASIC_INFORMATION)}
            invalidFields={invalidFields}
            updatedEmailAddress={updatedEmailAddress}
          />
        </div>
        {isUserSsoLoginForced && (
          <div className="section-wrapper">
            <RecoveryEmail
              ssoOrgSlug={ssoOrg?.slug}
              userEmails={userEmails}
              ssoOrgName={ssoOrgName}
              refetchUserEmails={refetchUserEmails}
            />
          </div>
        )}
        {showPersonalAccountSection && !isUserSsoLoginForced && (
          <div className="section-wrapper">
            <PersonalAccount
              updateStatus={this.getUpdateStatus(Sections.PERSONAL_ACCOUNT)}
              invalidFields={invalidFields}
              secondaryEmail={secondaryEmail}
              hasGeneratedPassword={hasGeneratedPassword}
            />

            {showPasswordSection && (
              <div className="password-section-wrapper">
                <Password updateStatus={this.getUpdateStatus(Sections.PASSWORD)} invalidFields={invalidFields} />
              </div>
            )}
          </div>
        )}
        <div className="section-wrapper">
          <JumioVerificationSettings data-test="cds-idv-view" />
        </div>
        {!showPersonalAccountSection && !isUserSsoLoginForced && showPasswordSection && (
          <div className="section-wrapper">
            <Password updateStatus={this.getUpdateStatus(Sections.PASSWORD)} invalidFields={invalidFields} />
          </div>
        )}
        {!isUserSsoLoginForced && (
          <>
            <div className="section-wrapper">
              <TwoFactorAuthentication
                state={twoFactorAuthenticationStatus}
                qrCode={twoFactorAuthenticationQRCode}
                invalidFields={twoFactorAuthenticationInvalidFields}
                updating={twoFactorAuthenticationIsUpdating}
                available
              />
            </div>
            <div className="section-wrapper">
              <SessionInvalidation />
            </div>
            <div className="section-wrapper">
              <AccountLinkingPreferences />
            </div>
          </>
        )}

        {showAccountDeletionSection && (
          <div className="section-wrapper">
            {/* @ts-expect-error ts-migrate(2322) FIXME: Type '{ userId: any; }' is not assignable to type ... Remove this comment to see the full error message */}
            <AccountDeletion userId={userId} />
          </div>
        )}
        <div className="section-wrapper">
          {/* @ts-expect-error ts-migrate(2322) FIXME: Type '{ userId: any; }' is not assignable to type ... Remove this comment to see the full error message */}
          <DataReport userId={userId} />
        </div>
      </div>
    );
  }

  renderPaymentSection() {
    const { creditCardPreferences, invalidFields, showDeleteCreditCardModal, activeWalletId, wallets, userId } =
      this.state;

    return (
      <CreditCardSettings
        creditCardPreferences={creditCardPreferences}
        showDeleteCreditCardModal={showDeleteCreditCardModal}
        activeWalletId={activeWalletId}
        wallets={wallets}
        updateStatusDelete={this.getUpdateStatus(Sections.CREDIT_CARD_DELETE)}
        invalidFields={invalidFields}
        userId={userId}
      />
    );
  }

  renderEmailSection() {
    const { notificationPreferences, emailPreferences } = this.state;

    return (
      emailPreferences && (
        <EmailPreferences
          globalNotificationsEnabled={notificationPreferences?.global === 'ENABLED'}
          coursePreferences={emailPreferences.courses}
          partnerPreferences={emailPreferences.partners}
          courseraPreferences={emailPreferences.coursera}
          courseUpdateStatus={this.getUpdateStatus(Sections.COURSE_EMAIL_PREFERENCES)}
          partnerUpdateStatus={this.getUpdateStatus(Sections.PARTNER_EMAIL_PREFERENCES)}
          courseraUpdateStatus={this.getUpdateStatus(Sections.COURSERA_EMAIL_PREFERENCES)}
        />
      )
    );
  }

  renderHighlightsSection() {
    const { userId } = this.state;
    // Page should not exist for a non user
    return <NotesAndHighlightingSettings userId={userId as number} />;
  }

  renderCalendarSection() {
    return <CalendarSettings />;
  }

  render() {
    const { isAccountDeleted, topSection } = this.state;
    const { TopSections } = SettingsConstants;

    if (isAccountDeleted) {
      return <DeletionConfirmation />;
    }

    return (
      <div className="c-account-settings">
        <CourseraMetatags metaNameAndProperties={{ title: 'My Account Settings', disableCrawlerIndexing: true }} />
        <PageHeader showShoppingCart showGDPRBanner showLanguagesDropdown hideMetaNav />
        <Grid container spacing={16} className="c-account-settings-body">
          <Grid
            item
            md={3}
            component="nav"
            aria-labelledby="a11y-navigation-heading"
            data-testid="account-settings-left-nav"
          >
            <MenuList> {this.renderNavigation()}</MenuList>
          </Grid>
          <Grid item md={9} component="main" className="c-account-settings-content" ref={this.settingsContentRef}>
            <article>
              {topSection === TopSections.ACCOUNT && this.renderAccountSection()}
              {topSection === TopSections.PAYMENT && this.renderPaymentSection()}
              {topSection === TopSections.EMAIL && this.renderEmailSection()}
              {topSection === TopSections.HIGHLIGHTS && this.renderHighlightsSection()}
              {topSection === TopSections.CALENDAR && this.renderCalendarSection()}
            </article>
          </Grid>
        </Grid>
        <PageFooter />
      </div>
    );
  }
}

const SettingsAppWrapper = (props: Props) => {
  return (
    <UserForcedSSOContextProvider>
      <UserForcedSSOContextConsumer>
        {({ isUserSsoLoginForced }) => <SettingsApp {...props} isUserSsoLoginForced={isUserSsoLoginForced} />}
      </UserForcedSSOContextConsumer>
    </UserForcedSSOContextProvider>
  );
};

type PropsFromFindUserEmailsByUserIdQuery = {
  userEmails?: UserEmail[];
};

export default compose<Props, {}>(
  setDisplayName('AccountSettingsHOC'),
  connectToRouter((router) => ({ router })),
  Retracked.createTrackedContainer(() => ({
    namespace: {
      app: 'account_settings',
      page: 'page',
    },
  })),
  connectToStores(['ApplicationStore'], ({ ApplicationStore }, props) => ({
    ...props,
    userData: ApplicationStore.getUserData(),
  })),
  // @ts-expect-error ts-migrate(2345) FIXME: Argument of type '{ props: ({ data }: { data: any;... Remove this comment to see the full error message
  waitForGraphQL(SettingsQuery, SettingsQueryOptions),
  waitForGraphQL<
    {},
    FindUserEmailsByUserIdQueryResult,
    FindUserEmailsByUserIdQueryVariables,
    PropsFromFindUserEmailsByUserIdQuery
  >(FindUserEmailsByUserIdQuery, {
    props: ({ data }) => {
      const userEmails = data?.FindUserEmailsByUserIdResponse?.userEmails || [];
      return { userEmails, refetchUserEmails: data?.refetch };
    },
    options: () => ({
      variables: {
        userId: `${user.get().id}`,
      },
      errorPolicy: 'all',
    }),
  }),
  // @ts-expect-error ts-migrate(2339) FIXME: Property 'thirdPartyAccounts' does not exist on ty... Remove this comment to see the full error message
  Naptime.createContainer(({ thirdPartyAccounts }) => {
    const ssoOrgId = thirdPartyAccounts
      ?.filter((_: $TSFixMe) => _.id === 'saml' || _.id === 'jwtInvite')
      ?.map((_: $TSFixMe) => _.identifier.split('|')[1])[0];

    return {
      gdprSettings:
        ssoOrgId &&
        GdprSettings.get(ssoOrgId, {
          fields: ['recoveryAccountInSettings'],
        }),
      ssoOrg: ssoOrgId
        ? ThirdPartyOrganizationsV1.get(ssoOrgId, {
            fields: ['name', 'slug'],
          })
        : undefined,
    };
  }),
  // @ts-expect-error ts-migrate(2345) FIXME: Argument of type '({ userEmails, thirdPartyOrganiz... Remove this comment to see the full error message
  withProps(({ userEmails, thirdPartyOrganizationIds, gdprSettings }) => {
    const showPersonalAccountSection =
      gdprSettings?.recoveryAccountInSettings !== false && thirdPartyOrganizationIds.length > 0;
    // GR-19243: hide account deletion for AXA learners
    const showAccountDeletionSection = !thirdPartyOrganizationIds.includes('_khIP87SRomhUydEliGbnw');
    const hasGeneratedPassword = user.get().generated_password;
    const secondaryEmail = getSecondaryEmail(userEmails);
    return {
      showPersonalAccountSection,
      showPasswordSection: !hasGeneratedPassword,
      showAccountDeletionSection,
      secondaryEmail,
      hasGeneratedPassword,
      userEmails,
    };
  }),
  withApollo
)(SettingsAppWrapper);
